import React, { useState, useEffect } from 'react';
import { generateReview } from '../utils/deepseek';

/**
 * ReviewStep Component
 * AI-powered review generator with Amazon redirect option
 */
const ReviewStep = ({ onAmazonRedirect, onClaimGifts, onReviewGenerated, onBack }) => {
  const [stars, setStars] = useState(5);
  const [tone, setTone] = useState('Enthousiaste');
  const [generatedReview, setGeneratedReview] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [hoveredStar, setHoveredStar] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const tones = ['Enthousiaste', 'Romantique', 'Amusant', 'Détaillé'];

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle star click
  const handleStarClick = (starIndex) => {
    setStars(starIndex);
  };

  // Handle review generation
  const handleGenerateReview = async () => {
    setIsGenerating(true);
    setError('');
    setCopySuccess(false);

    try {
      const review = await generateReview(stars, tone);
      setGeneratedReview(review);

      // Notify parent component about review generation
      if (onReviewGenerated) {
        onReviewGenerated({ stars, tone, reviewText: review });
      }
    } catch (err) {
      setError(err.message || 'Impossible de générer l\'avis. Veuillez réessayer.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle copy and redirect to Amazon
  const handleCopyAndRedirect = async () => {
    if (generatedReview) {
      try {
        await navigator.clipboard.writeText(generatedReview);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }

    // Call parent to show modal and redirect
    onAmazonRedirect();
  };

  // Handle review text change (user can edit)
  const handleReviewChange = (e) => {
    setGeneratedReview(e.target.value);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-light tracking-wide mb-4">
            Aidez-nous à grandir en laissant un avis
          </h2>
          <p className="text-lg text-gray-600">
            Utilisez notre assistant IA pour rédiger votre avis en quelques secondes (optionnel)
          </p>
        </div>

        {/* Review Generator Section */}
        <div className="space-y-8 mb-12">
          {/* Star Rating Selector */}
          <div>
            <label className="block text-sm font-light tracking-wide mb-4 text-gray-700 text-center">
              Comment évalueriez-vous votre expérience ?
            </label>
            <div className="flex justify-center space-x-3 md:space-x-4">
              {[1, 2, 3, 4, 5].map((starIndex) => (
                <button
                  key={starIndex}
                  type="button"
                  onClick={() => handleStarClick(starIndex)}
                  onMouseEnter={() => setHoveredStar(starIndex)}
                  onMouseLeave={() => setHoveredStar(null)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <svg
                    className={`w-12 h-12 md:w-16 md:h-16 transition-colors ${
                      starIndex <= (hoveredStar || stars)
                        ? 'text-black fill-current'
                        : 'text-gray-300 fill-current'
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </button>
              ))}
            </div>
            <p className="mt-3 text-sm text-gray-500 text-center">{stars} étoile{stars !== 1 ? 's' : ''}</p>
          </div>

          {/* Tone Selector */}
          <div>
            <label className="block text-sm font-light tracking-wide mb-3 text-gray-700">
              Choisissez le ton de votre avis
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {tones.map((toneOption) => (
                <button
                  key={toneOption}
                  type="button"
                  onClick={() => setTone(toneOption)}
                  className={`px-4 py-3 border transition-all duration-300 ${
                    tone === toneOption
                      ? 'border-black bg-black text-white'
                      : 'border-gray-300 bg-white text-black hover:border-black'
                  }`}
                >
                  {toneOption}
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <div>
            <button
              type="button"
              onClick={handleGenerateReview}
              disabled={isGenerating}
              className={`w-full px-8 py-4 text-lg font-light tracking-wide transition-all duration-300 ${
                isGenerating
                  ? 'bg-gray-400 text-white cursor-wait'
                  : 'bg-black text-white hover:bg-gray-800'
              } focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2`}
            >
              {isGenerating ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Génération de votre avis...
                </span>
              ) : (
                'Générer Mon Avis'
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Generated Review Display */}
          {generatedReview && (
            <div className="animate-fade-in">
              <label className="block text-sm font-light tracking-wide mb-3 text-gray-700">
                Votre avis généré (modifiable à souhait)
              </label>
              <textarea
                value={generatedReview}
                onChange={handleReviewChange}
                rows="8"
                className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-black transition-colors font-light leading-relaxed resize-none"
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-gray-500">
                  {generatedReview.length} caractères
                </p>
                {copySuccess && (
                  <p className="text-sm text-green-600 animate-fade-in">
                    ✓ Copié dans le presse-papier !
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-4 border-t border-gray-200 pt-8">
          {/* Submit Review to Amazon - Only shows after review is generated */}
          {generatedReview && (
            <div className="space-y-3">
              <p className="text-center text-base text-gray-700 font-light">
                Souhaitez-vous partager votre avis sur Amazon ? Cela nous aide énormément !
              </p>
              <button
                type="button"
                onClick={handleCopyAndRedirect}
                className="w-full px-8 py-4 bg-black text-white text-lg font-light tracking-wide
                         hover:bg-gray-800 transition-all duration-300
                         focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Soumettre Mon Avis sur Amazon
              </button>
              <p className="text-center text-sm text-gray-500 italic">
                (Optionnel, mais très apprécié)
              </p>
            </div>
          )}

          {/* Claim Free Gifts Button - Always visible and prominent */}
          <div className={generatedReview ? 'pt-6' : ''}>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onBack}
                className="w-16 py-4 border border-black bg-white text-black text-lg font-light tracking-wide
                         hover:bg-gray-100 transition-all duration-300
                         focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2
                         flex items-center justify-center"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                type="button"
                onClick={onClaimGifts}
                className="flex-1 px-6 py-3 bg-black text-white text-base font-light tracking-wide
                         hover:bg-gray-800 transition-all duration-300
                         focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
              >
                Réclamer Mes Cadeaux Gratuits
              </button>
            </div>
            <p className="text-center text-sm text-gray-500 pt-3">
              Aucun avis requis pour recevoir vos cadeaux
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
